import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';

import { Observable, filter, map } from 'rxjs';

import { IconLibrary } from '@finnairoyj/fcom-ui-styles';

import { CmsArticle, CmsPicture, CmsTaxonomyGeneral, CmsVideo } from '@fcom/core-api';
import { isNotBlank } from '@fcom/core/utils';
import { type IconName } from '@fcom/ui-components';

const allowedBackgrounds = ['nordic-blue-25-bg', 'nordic-blue-50-bg'];

@Component({
  selector: 'cms-rich-text',
  template: `
    <fin-rich-text
      [class]="'block ' + extraClasses + (bgClasses$ | async)"
      [content$]="content$"
      [headerLevel]="headerLevel"
      [headerStyles]="headerStyles"
      [icons$]="icons$"
      [media$]="media$"
      [scaleImage]="scaleImage"
      [styleClasses$]="styleClasses$"
      [title$]="title$"
    />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CmsRichTextComponent implements OnInit {
  @Input()
  content$: Observable<CmsArticle>;
  @Input()
  headerLevel: number;
  @Input()
  headerStyles = [];
  @Input()
  scaleImage = true;
  @Input()
  extraClasses = 'mr-large-b';

  title$: Observable<string>;
  media$: Observable<CmsPicture[] | CmsVideo[]>;
  icons$: Observable<Array<{ name: IconName; category?: IconLibrary }>>;
  styleClasses$: Observable<string[]>;
  bgClasses$: Observable<string>;

  ngOnInit(): void {
    this.title$ = this.content$.pipe(
      filter((content) => !content.subjectTaxonomyTags.some((tag) => tag === CmsTaxonomyGeneral.NO_TITLE)),
      map((t) => t.teaserTitle),
      filter(isNotBlank)
    );

    this.media$ = this.content$.pipe(
      // Mark response explicitly as array of CmsImageData or an Array of CmsVideo but not a mix
      map((content) => content?.media as CmsPicture[] | CmsVideo[]),
      filter((media) => media?.length > 0)
    );

    this.icons$ = this.content$.pipe(
      filter((content) => !!content.localSettings?.iconName),
      map((content) => {
        const names = content.localSettings?.iconName;
        const nameArr = Array.isArray(names) ? names : [names];
        const categories = content.localSettings?.iconCategory;
        const categoryArr = Array.isArray(categories) ? categories : [categories];
        return nameArr.map((name, index) => {
          return {
            name,
            category: categoryArr?.[index] ?? IconLibrary.SVG_LIBRARY,
          };
        });
      })
    );

    this.styleClasses$ = this.content$.pipe(
      map((content) => content.subjectTaxonomyTags),
      map((tags) =>
        tags.filter((tag) => {
          return tag.startsWith('cmstyle-');
        })
      )
    );

    this.bgClasses$ = this.content$.pipe(
      map((content) => content.subjectTaxonomyTags),
      map((tags) => {
        const bg = tags?.find((tag) => allowedBackgrounds.includes(tag));
        return bg ? ` ${bg} pr-small rounded-medium` : '';
      })
    );
  }
}
